import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Stack,
  CircularProgress,
  Menu,
  MenuItem,
  IconButton,
  Container,
  Dialog,
  DialogContent,
  Alert,
  Paper,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";

import CustomHeader from "../../components/common/CustomHeader";
import CustomTable from "../../components/common/CustomTable";
import CustomCard from "../../components/common/CustomCard";
import CustomInput from "../../components/common/CustomInput";
import CustomButton from "../../components/common/CustomButton";
import CustomPagination from "../../components/common/CustomPagination";
import { clientApi } from "../../api/tenantApi";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { EmailValidator } from "../../components/validator/EmailValidator";

/* ── Constants ───────────────────────────────────────────── */

const STATUS_CHIP = {
  active: <Chip label="Approved" color="success" size="small" sx={{ fontSize: "0.7rem", fontWeight: 700, height: 22 }} />,
  inactive: <Chip label="Pending" color="warning" size="small" sx={{ fontSize: "0.7rem", fontWeight: 700, height: 22 }} />,
  rejected: <Chip label="Rejected" color="error" size="small" sx={{ fontSize: "0.7rem", fontWeight: 700, height: 22 }} />,
};

/* ── Component ───────────────────────────────────────────── */

export default function TenantManagementPage() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Edit Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [emailError, setEmailError] = useState("");

  /* ── Fetch Tenants ─────────────────────────────────────── */

  const fetchTenants = useCallback(async (p = page, ps = pageSize) => {
    setLoading(true);
    try {
      const response = await clientApi.getTenants({ page: p, page_size: ps });
      const { status, data } = response || {};
      if (status === 200 && data) {
        setTenants(Array.isArray(data.results) ? data.results : []);
        setTotal(data?.pagination?.total ?? 0);
        setTotalPages(data?.pagination?.total_pages ?? 1);
        setPage(data?.pagination?.page ?? p);
      } else {
        setTenants([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchTenants(page, pageSize);
  }, [fetchTenants, page, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  /* ── Menu Handlers ───────────────────────────────────── */

  const openMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openEditModal = (row) => {
    setEditFormData({
      id: row.id,
      user_id: row.user_id || "",
      company_name: row.company_name || "",
      company_domain: row.company_domain || "",
      domain_alias: row.slug || "",
      plan_name: row.plan_name || "",
      status: row.status || "",
      full_name: row.full_name || "",
      email: row.email || "",
    });
    setEditError(null);
    setEmailError("");
    setEditModalOpen(true);
    closeMenu();
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditFormData(null);
    setEditError(null);
    setEmailError("");
  };

  const renderLabel = (label) => (
    <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.5px", display: "block" }}>
      {label}
    </Typography>
  );

  const handleFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "email") {
      const { isValid, errorMessage } = EmailValidator(value);
      setEmailError(isValid ? "" : errorMessage);
    }
  };

  const handleSaveEdit = useCallback(async () => {
    if (!editFormData) return;

    const { isValid: isEmailValid } = EmailValidator(editFormData.email);
    if (!isEmailValid) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      const payload = {
        tenant_id: editFormData.id,
        user_id: editFormData.user_id,
        company_name: editFormData.company_name,
        company_domain: editFormData.company_domain,
        slug: (editFormData.domain_alias || "").toLowerCase(),
        plan_name: editFormData.plan_name,
        status: editFormData.status,
        full_name: editFormData.full_name,
        email: editFormData.email,
      };

      const response = await clientApi.updateTenant(payload);

      if (response?.status === 200 || response?.status === 201) {
        closeEditModal();
        await fetchTenants();
      } else {
        setEditError(response?.message || "Failed to update tenant");
      }
    } catch (error) {
      console.error("Error updating tenant:", error);
      setEditError("An error occurred while updating the tenant");
    } finally {
      setEditLoading(false);
    }
  }, [editFormData, fetchTenants]);

  /* ── Filtered Rows ───────────────────────────────────── */

  const filteredRows = useMemo(() => {
    if (filter === "all") return tenants;
    return tenants.filter((t) => t.status === filter);
  }, [tenants, filter]);

  /* ── Table Columns ───────────────────────────────────── */

  const columns = useMemo(
    () => [
      {
        label: "Tenant Name",
        field: "company_name",
        stickyFirst: true,
        headerAlign: "center",
        align: "left",

        render: (_, row) => (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
          >
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.contrastText,
                width: 32,
                height: 32,
                mr:1,
                fontSize: "0.8rem",
                fontWeight: 700,
              }}
            >
              {row.company_name?.slice(0, 2).toUpperCase()}
            </Avatar>

            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "text.primary",
                textAlign: "left",
              }}
            >
              {row.company_name}
            </Typography>
          </Stack>
        ),
      },
      { label: "Admin Name", field: "full_name", align: "center" },
      { label: "Admin Email", field: "email", align: "center" },
      { label: "Alias", field: "slug", align: "center" },
      { label: "Website", field: "company_domain", align: "center" },
      {
        label: "Plan Name",
        field: "plan_name",
        align: "center",
        render: (value) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Chip
              label={value?.toUpperCase() || "N/A"}
              variant="outlined"
              size="small"
              sx={{ fontSize: "0.7rem", fontWeight: 700, height: 22, borderColor: alpha(theme.palette.divider, 0.5) }}
            />
          </Box>
        ),
      },
      {
        label: "Status",
        field: "status",
        align: "center",
        render: (value) => (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {STATUS_CHIP[value] ?? <Chip label={value} size="small" sx={{ fontSize: "0.7rem", height: 22 }} />}
          </Box>
        ),
      },
      {
        label: "Created",
        field: "created_at",
        align: "center",
        render: (value) => {
          if (!value) return "—";
          return new Date(value).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          });
        },
      },
      {
        label: "Action",
        field: "action",
        stickyLast: true,
        render: (_, row) => {
          return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <IconButton onClick={(e) => openMenu(e, row)} size="small" sx={{ padding: 0.5 }}>
                <MoreVertIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [theme.palette.primary.main, theme.palette.divider]
  );

  /* ── UI ───────────────────────────────────────────────── */

  return (
    <Box sx={{ minHeight: "100vh", pb: 4 }}>
      <CustomHeader title="Tenant Management" />

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        {/* Table Wrapper targets generic table elements to force compactness and fix scrolling overlap */}
          {loading ? (
            <Box sx={{ p: 2 }}>
              <SkeletonLoader type="table" rows={5} columns={columns.map((c) => c.label)} />
            </Box>
          ) : (
            <>
              {/* Ensure CustomTable does not apply its own background opacity to headers */}
              <CustomTable columns={columns} rows={filteredRows} />

              {/* No wrapper borders needed here, the CustomPagination Divider handles it */}
              <CustomPagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
      </Container>

      {/* ── Action Menu ───────────────────────────────── */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 150,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
            "& .MuiMenuItem-root": { fontSize: "0.85rem", py: 1, fontWeight: 500 },
          },
        }}
      >
        <MenuItem onClick={() => openEditModal(selectedRow)}>Edit Profile</MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/tenant/token/detail", {
              state: {
                tenantId: selectedRow?.id,
                tenant_name: selectedRow?.company_name,
              },
            });
            closeMenu();
          }}
        >
          View Token Usage
        </MenuItem>
      </Menu>

      {/* ── Edit Tenant Modal ─────────────────────────── */}
      <Dialog
        open={editModalOpen}
        onClose={closeEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, backgroundImage: "none" },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <CustomCard border={false} sx={{ borderRadius: 0, m: 0 }}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Edit Tenant Configuration
              </Typography>
            </Box>

            {editError && (
              <Alert severity="error" sx={{ mb: 3, py: 0, '& .MuiAlert-message': { fontSize: "0.85rem", fontWeight: 500 } }}>
                {editError}
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 2,
                overflowY: "auto",
              }}
            >
              <Box>
                {renderLabel("Tenant Name")}
                <CustomInput
                  name="company_name"
                  type="text"
                  size="small"
                  value={editFormData?.company_name || ""}
                  onChange={(e) => handleFormChange("company_name", e.target.value)}
                  fullWidth
                  disabled={true}
                  sx={{ '& input': { fontSize: '0.85rem', py: 1 } }}
                />
              </Box>

              <Box>
                {renderLabel("Company Domain")}
                <CustomInput
                  name="company_domain"
                  type="text"
                  size="small"
                  value={editFormData?.company_domain || ""}
                  onChange={(e) => handleFormChange("company_domain", e.target.value)}
                  fullWidth
                  disabled={editLoading}
                  sx={{ '& input': { fontSize: '0.85rem', py: 1 } }}
                />
              </Box>

              <Box>
                {renderLabel("Admin Name")}
                <CustomInput
                  name="full_name"
                  type="text"
                  size="small"
                  value={editFormData?.full_name || ""}
                  onChange={(e) => handleFormChange("full_name", e.target.value)}
                  fullWidth
                  disabled={true}
                  sx={{ '& input': { fontSize: '0.85rem', py: 1 } }}
                />
              </Box>

              <Box>
                {renderLabel("Admin Email")}
                <CustomInput
                  name="email"
                  type="email"
                  size="small"
                  value={editFormData?.email || ""}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  error={!!emailError}
                  helperText={emailError}
                  fullWidth
                  disabled={true}
                  sx={{ '& input': { fontSize: '0.85rem', py: 1 } }}
                />
              </Box>

              <Box>
                {renderLabel("Domain Alias")}
                <CustomInput
                  name="domain_alias"
                  type="text"
                  size="small"
                  value={editFormData?.domain_alias || ""}
                  onChange={(e) => handleFormChange("domain_alias", e.target.value)}
                  fullWidth
                  disabled={editLoading}
                  sx={{ '& input': { fontSize: '0.85rem', py: 1 } }}
                />
              </Box>

              <Box>
                {renderLabel("Plan")}
                <CustomInput
                  select
                  name="plan_name"
                  size="small"
                  value={editFormData?.plan_name || ""}
                  disabled={true}
                  fullWidth
                  sx={{ '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}
                >
                  <option value="">Select Plan</option>
                  <option value={editFormData?.plan_name}>{editFormData?.plan_name}</option>
                </CustomInput>
              </Box>

              <Box sx={{ gridColumn: { md: "span 2" } }}>
                {renderLabel("Status")}
                <CustomInput
                  select
                  name="status"
                  size="small"
                  value={editFormData?.status || ""}
                  disabled={true}
                  fullWidth
                  sx={{ '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}
                >
                  <option value="active">Approved</option>
                  <option value="inactive">Pending</option>
                  <option value="rejected">Rejected</option>
                </CustomInput>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                pt: 2.5,
                mt: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CustomButton onClick={closeEditModal} disabled={editLoading} variant="outlined" size="small">
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                size="small"
                onClick={handleSaveEdit}
                disabled={editLoading}
                startIcon={editLoading ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </CustomButton>
            </Box>
          </CustomCard>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
